<template>
  <div>
    <LoaderSpinner v-if="loadingSave" />

    <ModalConsulte
      ref="modalExcluir"
      :hide-footer="true"
      :hide-header="true"
      modalWidth="300px !important"
      tituloAcessivel="Confirmar exclusão do registro"
    >
      <template #content>
        <div class="modal-content">
          <div class="modal-body p-4 text-center">
            <i class="ri-alert-line h1 text-warning" aria-hidden="true"></i>
            <h2 class="h4 mt-2">Atenção!</h2>
            <p class="mt-3">Deseja realmente excluir o registro selecionado?</p>
            <!-- "Sim"/"Não" sozinhos nao dizem a que pergunta respondem quando o leitor
                 de tela navega botao a botao; o aria-label completa a acao. -->
            <button type="button" class="btn btn-warning my-2 mx-2" @click="onExcluir"
              aria-label="Sim, excluir o registro">Sim</button>
            <button type="button" class="btn btn-secondary my-2 mx-2" @click="closeModal"
              aria-label="Não excluir, voltar">Não</button>
          </div>
        </div>
      </template>
    </ModalConsulte>

    <div :class="['button-list', { 'button-list-fixed': fixarBotoes }]" role="group"
      aria-label="Ações do registro">
      <div class="button-container">
        <button
          v-if="showLimparInterno"
          type="button"
          class="btn btn-secondary btn-action"
          @click.prevent="onLimpar"
          title="Criar novo registro"
          aria-label="Criar novo registro"
        >
          <i class="uil-plus" aria-hidden="true"></i>
          <span class="btn-text">Novo</span>
        </button>

        <button
          v-if="showSalvarInterno"
          type="submit"
          class="btn btn-primary btn-action"
          @click="onSalvar"
          title="Salvar alterações"
          aria-label="Salvar alterações"
        >
          <i class="ri-save-line" aria-hidden="true"></i>
          <span class="btn-text">Salvar</span>
        </button>

        <router-link
          v-if="showPesquisarInterno"
          :to="gridViewLink"
          class="btn btn-warning btn-action"
          title="Pesquisar registros"
          aria-label="Pesquisar registros"
        >
          <i class="uil-search" style="color: white" aria-hidden="true"></i>
          <span class="btn-text" style="color: white">Pesquisar</span>
        </router-link>

        <button
          v-if="showImprimirInterno"
          type="button"
          class="btn btn-info btn-action"
          @click.prevent="onImprimir"
          title="Imprimir"
          aria-label="Imprimir"
        >
          <i class="uil-print" aria-hidden="true"></i>
          <span class="btn-text">Imprimir</span>
        </button>

        <button
          v-if="showExcluirInterno"
          type="button"
          class="btn btn-danger btn-action"
          @click.prevent="popupExcluir"
          title="Excluir registro"
          aria-label="Excluir registro"
        >
          <i class="uil-trash-alt" aria-hidden="true"></i>
          <span class="btn-text">Excluir</span>
        </button>

        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import LoaderSpinner from '@/components/loader-spinner/LoaderSpinner.vue';
  import ModalConsulte from '@/components/modal/ModalConsulte.vue';
  import { eTipoPermissao } from '@/enums/ePermissoes';
  import router from '@/routes';
  import sessionService from '@/services/helper/session.service';
  import logService from '@/services/log/log.service';
  import { computed, defineComponent, getCurrentInstance, onMounted, ref, watch } from 'vue';

  export default defineComponent({
    name: 'BarraBotoes',
    components: { LoaderSpinner, ModalConsulte },
    props: {
      showLimpar: { type: Boolean, default: true },
      showSalvar: { type: Boolean, default: false },
      showExcluir: { type: Boolean, default: false },
      showPesquisar: { type: Boolean, default: true },
      showImprimir: { type: Boolean, default: true },
      bloquearBotaoSalvar: { type: Boolean, default: false },
      bloquearBotaoExcluir: { type: Boolean, default: false },
      loadingSave: { type: Boolean, default: false },
      gridViewLink: { type: String, default: '/' },
      idRegistroCarregado: { type: String, default: '' },
      fixarBotoes: { type: Boolean, default: false },
    },
    emits: ['limpar', 'excluir', 'imprimir'],
    setup(props, { emit }) {
      /*
       * QUEM ESCUTA @imprimir MANDA NA IMPRESSÃO.
       *
       * Antes o botão chamava `window.print()` SEMPRE e só depois emitia o evento. Numa tela que
       * trata o próprio Imprimir — a ficha do estudante, por exemplo — isso abria o diálogo do
       * navegador imprimindo a PÁGINA (com menu lateral, campos de formulário e caixas de
       * seleção) por cima do relatório que a tela ia gerar. O usuário via a captura da tela onde
       * esperava o documento.
       *
       * `vnode.props.onImprimir` existe quando o pai declarou `@imprimir`. Havendo tratador, o
       * componente só avisa e sai do caminho; sem tratador, mantém o comportamento antigo, que é
       * o que as telas sem relatório próprio ainda usam.
       */
      const instancia = getCurrentInstance();
      const paiTrataImpressao = computed(() => Boolean(instancia?.vnode?.props?.onImprimir));
      const showSalvarInterno = ref(props.showSalvar);
      const showExcluirInterno = ref(props.showExcluir);
      const showLimparInterno = ref(props.showLimpar);
      const showPesquisarInterno = ref(props.showPesquisar);
      const showImprimirInterno = ref(props.showImprimir);

      const modalExcluir = ref<InstanceType<typeof ModalConsulte> | null>(null);

      const atualizarPermissoes = () => {
        const permissaoTela = router.currentRoute.value.meta?.permissions;
        const permissaoAcaoTela = sessionService.RetornarPermissaoAcaoTela(
          permissaoTela as eTipoPermissao[],
        );

        showExcluirInterno.value =
          permissaoAcaoTela.excluir &&
          props.idRegistroCarregado !== undefined &&
          props.idRegistroCarregado !== '';

        /*
         * "Novo" limpa o formulario para CRIAR um registro: e a permissao de incluir.
         *
         * Ele era o unico dos cinco botoes que nunca consultava a permissao — ficava visivel
         * para um grupo "somente visualiza", que clicava, preenchia tudo e so descobria no fim
         * que nao ha Salvar. Nao e um furo de gravacao (sem `incluir` o Salvar ja nao aparece),
         * e sim trabalho perdido e a impressao de que a permissao nao funciona.
         *
         * `props.showLimpar` continua mandando: tela que esconde o Novo por regra propria segue
         * escondendo, e quem tem `incluir` nao perde nada. Suporte/desenvolvimento e telas sem
         * permissao configurada recebem `incluir = true` do proprio RetornarPermissaoAcaoTela.
         */
        showLimparInterno.value = props.showLimpar && permissaoAcaoTela.incluir;
        showSalvarInterno.value =
          props.idRegistroCarregado === undefined || props.idRegistroCarregado === ''
            ? permissaoAcaoTela.incluir
            : permissaoAcaoTela.alterar;

        if (props.bloquearBotaoSalvar) showSalvarInterno.value = false;
        if (props.bloquearBotaoExcluir) showExcluirInterno.value = false;
      };

      onMounted(() => {
        atualizarPermissoes();
      });

      watch(
        () => props.idRegistroCarregado,
        () => atualizarPermissoes(),
      );

      watch(
        () => [props.bloquearBotaoSalvar, props.bloquearBotaoExcluir],
        () => atualizarPermissoes(),
      );

      // showLimpar (o botao "Novo") era lido UMA vez, no setup, e nunca mais: telas que decidem
      // esconder o Novo depois da montagem -- por exemplo esperando a resposta de um endpoint de
      // permissao -- passavam o prop novo e o botao continuava na tela. As outras nove telas que
      // usam este prop passam um literal, entao para elas nada muda.
      watch(
        () => props.showLimpar,
        () => atualizarPermissoes(),
      );

      // Item 49 do edital -- registra o clique nas acoes do formulario (BarraBotoes e
      // compartilhado por praticamente todo cadastro simples do sistema, entao isso cobre
      // muitas telas de uma vez, sem instrumentar tela por tela). E o clique em si, nao o
      // resultado -- BarraBotoes so emite o evento pro componente pai tratar, nao sabe se
      // Salvar/Excluir realmente terminou com sucesso.
      const registrarAcaoBotao = (acao: string, tipoEvento: string) => {
        const rota = router.currentRoute.value.path;
        logService.registrarAtividade(tipoEvento, `Clicou em "${acao}" (${rota})`, {
          rota,
          modulo: 'Formulario',
        });
      };

      const onLimpar = () => {
        registrarAcaoBotao('Novo', 'ACAO_NOVO');
        emit('limpar');
      };
      const onSalvar = () => registrarAcaoBotao('Salvar', 'ACAO_SALVAR');
      const onExcluir = () => {
        registrarAcaoBotao('Excluir', 'ACAO_EXCLUIR');
        emit('excluir');
        closeModal();
      };
      const onImprimir = () => {
        registrarAcaoBotao('Imprimir', 'ACAO_IMPRIMIR');

        if (paiTrataImpressao.value) {
          emit('imprimir');
          return;
        }

        window.print();
      };
      const popupExcluir = () => modalExcluir.value?.open();
      const closeModal = () => modalExcluir.value?.close();

      return {
        showSalvarInterno,
        showExcluirInterno,
        showLimparInterno,
        showPesquisarInterno,
        showImprimirInterno,
        modalExcluir,
        onLimpar,
        onSalvar,
        onExcluir,
        onImprimir,
        popupExcluir,
        closeModal,
      };
    },
  });
</script>

<style scoped>
  .button-list {
    padding: 10px;
    transition: all 0.3s ease;
  }

  .button-list-fixed {
    position: fixed;
    bottom: 0;
    left: 260px;
    right: 0;
    width: auto;
    box-sizing: border-box;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 1));
    backdrop-filter: blur(10px);
    box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    padding: 15px 20px;
    border-top: 2px solid #e9ecef;
    margin-left: 0px;
  }

  html[data-sidenav-size='compact']:not(.authentication-bg) .button-list-fixed {
    left: 160px;
  }

  html[data-sidenav-size='condensed']:not(.authentication-bg) .button-list-fixed,
  html[data-sidenav-size='sm-hover']:not(.authentication-bg) .button-list-fixed {
    left: 70px;
  }

  html[data-sidenav-size='full']:not(.authentication-bg) .button-list-fixed {
    left: 0;
  }

  .button-container {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: center;
    max-width: 100%;
    margin: 0;
  }

  .btn-action,
  :deep(.btn-action) {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 500;
    border-radius: 8px;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    border: none;
    min-width: 120px;
    justify-content: center;
  }

  .btn-action:hover,
  :deep(.btn-action:hover) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  .btn-action:active,
  :deep(.btn-action:active) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  .btn-action i,
  :deep(.btn-action i) {
    font-size: 18px;
  }

  /*
   * O `:deep` aqui e no media query abaixo NAO e cosmetico.
   *
   * Estilo com `scoped` casa pelo data-v do componente, e conteudo que chega pelo <slot> carrega
   * o data-v do PAI — por isso as regras de `.btn-action` ja vinham duplicadas com `:deep`. A de
   * `.btn-text` tinha ficado de fora: em telas estreitas os botoes internos (Novo, Salvar,
   * Pesquisar, Excluir) encolhiam para so o icone e os botoes vindos do slot ("Copiar Dados
   * Grupo Padrao", "Reset senha") continuavam com o rotulo inteiro — quebravam a linha e
   * ficavam com o dobro da largura dos vizinhos. Com `:deep`, quem vem pelo slot segue a mesma
   * regra, desde que envolva o rotulo em <span class="btn-text">.
   */
  .btn-text,
  :deep(.btn-text) {
    font-weight: 600;
  }

  .btn-secondary {
    background: linear-gradient(135deg, #6c757d 0%, #5a6268 100%);
    color: white;
  }

  .btn-secondary:hover {
    background: linear-gradient(135deg, #5a6268 0%, #545b62 100%);
  }

  .btn-primary {
    background: linear-gradient(135deg, #727cf5 0%, #6168e7 100%);
    color: white;
  }

  .btn-primary:hover {
    background: linear-gradient(135deg, #6168e7 0%, #5761e3 100%);
  }

  .btn-warning,
  :deep(.btn-warning) {
    background: linear-gradient(135deg, #ffbc00 0%, #f0b400 100%);
    color: #212529;
    text-decoration: none;
  }

  .btn-warning:hover,
  :deep(.btn-warning:hover) {
    background: linear-gradient(135deg, #f0b400 0%, #e0a800 100%);
    color: #212529;
  }

  .btn-info,
  :deep(.btn-info) {
    background: linear-gradient(135deg, #0acf97 0%, #09b886 100%);
    color: white;
  }

  /*
   * A COR DO TEXTO PRECISA SER REAFIRMADA EM CADA ESTADO.
   *
   * O Bootstrap pinta o texto do `.btn-info` de PRETO em :hover, :focus e :active — a variante
   * "info" dele pressupõe fundo claro. Aqui o fundo é o degradê verde, e a regra acima só
   * garantia o branco em repouso: bastava passar o mouse para o rótulo virar preto sobre verde.
   *
   * Além de feio, é problema de contraste (WCAG 1.4.3/1.4.6): o estado de foco é justamente
   * quando o texto precisa estar mais legível, não menos.
   *
   * Aparecia primeiro nos botões vindos pelo <slot> (o "Relatórios" da ficha do estudante), que
   * dependem só das regras `:deep`, mas o mesmo furo existia para os botões internos.
   */
  .btn-info:hover,
  .btn-info:focus,
  .btn-info:focus-visible,
  .btn-info:active,
  :deep(.btn-info:hover),
  :deep(.btn-info:focus),
  :deep(.btn-info:focus-visible),
  :deep(.btn-info:active) {
    background: linear-gradient(135deg, #09b886 0%, #08a177 100%);
    color: white;
  }

  .btn-danger {
    background: linear-gradient(135deg, #fa5c7c 0%, #f24968 100%);
    color: white;
  }

  .btn-danger:hover {
    background: linear-gradient(135deg, #f24968 0%, #ea3d5a 100%);
  }

  @media (max-width: 768px) {
    .button-list-fixed {
      left: 0;
      padding: 12px 15px;
    }

    .btn-action,
    :deep(.btn-action) {
      min-width: 100px;
      padding: 8px 16px;
      font-size: 13px;
    }

    .btn-action i {
      font-size: 16px;
    }

    .button-container {
      gap: 8px;
    }
  }

  @media (max-width: 576px) {
    .btn-text,
    :deep(.btn-text) {
      display: none;
    }

    .btn-action,
    :deep(.btn-action) {
      min-width: auto;
      padding: 10px 15px;
    }

    .button-container {
      justify-content: center;
    }
  }
</style>
